import { createRouter, createWebHistory } from 'vue-router';
import type { RouteRecordRaw } from 'vue-router';
import {
  applyRouteAppHost,
  clearAppStatusBarPadding,
  initAppHostParams,
  shouldApplyAppStatusBarPadding
} from '@/utils/app-host';
import { setToken } from '@/utils/auth';

const history = createWebHistory();

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: '/home'
  },
  {
    path: '/login',
    name: 'AppLogin',
    meta: {
      title: '登录',
      // 去「绑定工号」指引再返回时保留手机号/验证码/密码
      keepAlive: true
    },
    component: () => import('@/views/app-login/index.vue')
  },
  {
    path: '/mp/entry',
    name: 'MpEntry',
    meta: {
      title: '登录中'
    },
    component: () => import('@/views/mp/entry/index.vue')
  },
  {
    path: '/login/bind-employee',
    name: 'BindEmployeeGuide',
    meta: {
      title: '经代公司专属平台工号绑定'
    },
    component: () => import('@/views/app-login/bind-employee.vue')
  },
  {
    path: '/onboarding/verify',
    name: 'OnboardingVerify',
    meta: {
      title: '在线入职'
    },
    component: () => import('@/views/login/index.vue')
  },
  {
    path: '/onboarding/form',
    name: 'OnboardingForm',
    meta: {
      title: '基本信息填写'
    },
    component: () => import('@/views/onboarding/index.vue')
  },
  {
    path: '/onboarding/result',
    name: 'OnboardingResult',
    meta: {
      title: '申请成为代理人'
    },
    component: () => import('@/views/onboarding/result.vue')
  },
  {
    path: '/home',
    name: 'Home',
    meta: {
      title: '首页',
      keepAlive: true
    },
    component: () => import('@/views/home/index.vue')
  },
  {
    path: '/insurance-plan',
    name: 'InsurancePlan',
    meta: { title: '计划书' },
    children: [
      {
        path: 'list',
        name: 'PlanList',
        meta: { title: '计划书列表', keepAlive: true },
        component: () => import('@/insurance-plan/views/plan-list/index.vue')
      },
      {
        path: 'chuangke/list',
        name: 'MakerPlanList',
        meta: { title: '创客计划书列表', keepAlive: true },
        component: () => import('@/insurance-plan/views/maker-plan-list/index.vue')
      },
      {
        path: 'proposal-config',
        name: 'PlanConfig',
        meta: { title: '计划书配置' },
        component: () => import('@/insurance-plan/views/plan-config/index.vue')
      },
      {
        path: 'product-trial',
        redirect: (to) => ({
          name: 'PlanConfig',
          query: to.query,
          hash: to.hash
        })
      },
      {
        path: 'product-case',
        name: 'ProductCase',
        meta: { title: '产品案例' },
        component: () => import('@/insurance-plan/views/product-case/index.vue')
      },
      {
        path: 'maker/proposal-config',
        name: 'MakerPlanConfig',
        meta: { title: '创客计划书配置' },
        component: () => import('@/insurance-plan/views/maker-plan-config/index.vue')
      },
      {
        path: 'points-calculate',
        name: 'PointsCalculate',
        meta: { title: '积分计算' },
        component: () => import('@/insurance-plan/views/points-calculate/index.vue')
      },
      {
        path: 'proposal',
        name: 'Proposal',
        meta: { title: '计划书预览' },
        component: () => import('@/insurance-plan/views/proposal/index.vue')
      },
      {
        path: 'market/proposal',
        name: 'MarketProposal',
        meta: { title: '创客计划书预览' },
        component: () => import('@/insurance-plan/views/market-proposal/index.vue')
      },
      {
        path: 'review',
        name: 'Review',
        meta: { title: '计划书审阅' },
        component: () => import('@/insurance-plan/views/review/index.vue')
      },
      {
        path: 'plan-behavior',
        name: 'PlanBehavior',
        meta: { title: '计划方案' },
        component: () => import('@/insurance-plan/views/plan-behavior/index.vue')
      },
      {
        path: 'proposal-trace-list',
        name: 'ProposalTraceList',
        meta: { title: '轨迹查看' },
        component: () => import('@/insurance-plan/views/proposal-trace-list/index.vue')
      },
      {
        path: 'proposal-playback',
        name: 'ProposalPlayback',
        meta: { title: '计划书回溯' },
        component: () => import('@/insurance-plan/views/proposal-playback/index.vue')
      },
      {
        path: 'proposal-visitors',
        name: 'ProposalVisitors',
        meta: { title: '访客管理' },
        children: [
          {
            path: 'list',
            name: 'VisitorList',
            meta: { title: '全部访客' },
            component: () => import('@/insurance-plan/views/proposal-visitors/VisitorList.vue')
          },
          {
            path: 'detail',
            name: 'VisitorDetail',
            meta: { title: '访客详情' },
            component: () => import('@/insurance-plan/views/proposal-visitors/VisitorDetail.vue')
          }
        ]
      },
      {
        path: 'customer',
        name: 'Customer',
        meta: { title: '客户管理' },
        children: [
          {
            path: 'list',
            name: 'CustomerList',
            meta: { title: '关联客户' },
            component: () => import('@/insurance-plan/views/customer/CustomerList.vue')
          },
          {
            path: 'search',
            name: 'CustomerSearchHistory',
            meta: { title: '客户搜索' },
            component: () => import('@/insurance-plan/views/customer/CustomerSearchHistory.vue')
          },
          {
            path: 'search-result',
            name: 'CustomerSearchResult',
            meta: { title: '搜索结果' },
            component: () => import('@/insurance-plan/views/customer/CustomerSearchResult.vue')
          },
          {
            path: 'detail',
            name: 'CustomerDetail',
            meta: { title: '客户详情' },
            component: () => import('@/insurance-plan/views/customer/CustomerDetail.vue')
          }
        ]
      }
    ]
  },
  {
    path: '/user/card',
    name: 'UserCard',
    meta: {
      title: '我的名片'
    },
    component: () => import('@/views/user/card/index.vue')
  },
  {
    path: '/course/detail',
    name: 'CourseDetail',
    meta: {
      title: '课程详情'
    },
    component: () => import('@/views/course/detail/index.vue')
  },
  {
    path: '/headline/detail',
    name: 'HeadlineDetail',
    meta: {
      title: '头条详情'
    },
    component: () => import('@/views/headline/detail/index.vue')
  },
  {
    path: '/product-compare',
    name: 'ProductCompare',
    meta: {
      title: '产品对比'
    },
    component: () => import('@/product-compare/views/index.vue')
  },
  {
    path: '/product-compare/product',
    name: 'ProductCompareProduct',
    meta: {
      title: '产品对比'
    },
    component: () => import('@/product-compare/views/product/index.vue')
  },
  {
    path: '/product-compare/product/select',
    name: 'ProductCompareProductSelect',
    meta: {
      title: '产品选择'
    },
    component: () => import('@/product-compare/views/product/select.vue')
  },
  {
    path: '/product-compare/plan',
    name: 'ProductComparePlan',
    meta: {
      title: '方案对比'
    },
    component: () => import('@/product-compare/views/plan/index.vue')
  },
  {
    path: '/product-compare/plan/select',
    name: 'ProductComparePlanSelect',
    meta: {
      title: '方案选择'
    },
    component: () => import('@/product-compare/views/plan/select.vue')
  },
  {
    path: '/product-compare/result',
    name: 'ProductCompareResult',
    meta: {
      title: '对比结果'
    },
    component: () => import('@/product-compare/views/result/index.vue')
  },
  {
    path: '/financial-safety',
    name: 'FinancialSafety',
    meta: {
      title: 'FSP财务安全规划'
    },
    component: () => import('@/financial-safety/views/index.vue')
  },
  {
    path: '/financial-safety/wizard',
    name: 'FinancialSafetyWizard',
    meta: {
      title: '财务安全测评'
    },
    component: () => import('@/financial-safety/views/wizard.vue')
  },
  {
    path: '/financial-safety/report',
    name: 'FinancialSafetyReport',
    meta: {
      title: 'FSP财务安全规划报告书'
    },
    component: () => import('@/financial-safety/views/report.vue')
  },
  {
    path: '/financial-safety/share',
    name: 'FinancialSafetyShare',
    meta: {
      title: 'FSP财务安全规划报告书'
    },
    component: () => import('@/financial-safety/views/share.vue')
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    meta: {
      title: '页面不存在'
    },
    component: () => import('@/views/not-found/index.vue')
  }
];

const router = createRouter({
  history,
  routes
});

initAppHostParams();

router.beforeEach((to, _from, next) => {
  const token = new URLSearchParams(window.location.search).get('token');
  if (token) {
    setToken(token);
  }
  if (to.meta.title) {
    document.title = to.meta.title as string;
  }
  if (shouldApplyAppStatusBarPadding(to.path)) {
    applyRouteAppHost(to.path);
  } else {
    clearAppStatusBarPadding();
  }
  next();
});

router.afterEach(() => {
  window.scrollTo(0, 0);
});

export default router;
